/**
 * My Day — the sales consultant's landing screen.
 *
 * "At the start of the system, on the left-hand side it really should be all
 * the tasks for that person to do for that day." Everything here comes from
 * tasksService.workplan(), an endpoint that already existed and that nothing
 * called.
 *
 * The "no next step" panel is the other half of the requirement: every open
 * lead is meant to have a next action, and this is where a consultant clears
 * the ones that don't.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import TopBar from '../components/TopBar';
import NextEventModal from '../components/NextEventModal';
import CompleteTaskModal from '../components/CompleteTaskModal';
import { tasksService } from '../lib/services';
import { useApi } from '../lib/hooks/useApi';
import { useToast } from '../lib/toast';
import { useCurrentUser } from '../lib/currentUser';
import { useDirectoryNames } from '../lib/useDirectoryNames';
import {
  formatTime,
  formatDate,
  relativeDue,
  orgCivilToday,
  orgAddDays,
  civilToISODate,
  isoDateToCivil,
  isOrgToday,
} from '../lib/datetime';
import { typeMeta } from '../lib/appointmentMeta';

const EMPTY_PLAN = {
  overdue: [], morning: [], afternoon: [], later: [],
  appointments: [], leadsWithoutNextEvent: [],
  counts: { dueToday: 0, overdue: 0, appointments: 0, leadsWithoutNextEvent: 0 },
};

const asList = (v) => (Array.isArray(v) ? v : (v?.content ?? []));

/**
 * @param embedded  true when rendered inside the consultant dashboard — suppresses
 *                  the TopBar and the stat row, which the host already shows.
 */
export default function MyDay({ embedded = false } = {}) {
  const { toast } = useToast();
  const { consultantId, user } = useCurrentUser();
  const names = useDirectoryNames();

  const [civil, setCivil] = useState(() => orgCivilToday());
  const dateParam = civilToISODate(civil);

  const [completing, setCompleting] = useState(null);   // task
  const [settingFor, setSettingFor] = useState(null);   // lead

  const { data, loading, refetch } = useApi(
    () => tasksService.workplan({ consultantId, date: dateParam }),
    { fallback: EMPTY_PLAN, deps: [consultantId, dateParam], enabled: !!consultantId },
  );

  const plan = useMemo(() => ({ ...EMPTY_PLAN, ...(data || {}) }), [data]);

  const counts = plan.counts || EMPTY_PLAN.counts;
  const today = isOrgToday(civil);

  const patch = async (task, body, okMsg) => {
    try {
      await tasksService.update(task.id, body);
      toast(okMsg, { variant: 'success' });
      refetch();
    } catch (e) {
      toast(`Update failed: ${e?.response?.data?.message || e?.message || 'unknown error'}`,
        { variant: 'danger' });
    }
  };

  const snooze = (t) => {
    const base = Math.max(new Date(t.dueAt || Date.now()).getTime(), Date.now());
    patch(t, { dueAt: new Date(base + 86_400_000).toISOString() }, 'Snoozed a day');
  };

  if (!consultantId) {
    return (
      <>
        {!embedded && <TopBar crumbs={['My Day']} />}
        <div className={embedded ? '' : 'app-content'}>
          <div className="card" style={{ padding: 24 }}>
            <div className="empty-state">
              <div className="icon">👤</div>
              <div className="title">Not signed in</div>
              <div className="desc">My Day shows the tasks assigned to you, so it needs a signed-in user.</div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const nav = (
          <div className="flex-row" style={{ gap: 6, alignItems: 'center' }}>
            {loading && <span className="mono text-xs text-muted">loading…</span>}
            <button type="button" className="btn small" onClick={() => setCivil(orgAddDays(civil, -1))} aria-label="Previous day">‹</button>
            <button type="button" className={`btn small${today ? ' primary' : ''}`} onClick={() => setCivil(orgCivilToday())}>Today</button>
            <button type="button" className="btn small" onClick={() => setCivil(orgAddDays(civil, 1))} aria-label="Next day">›</button>
            <input
              type="date"
              value={dateParam}
              onChange={(e) => { const c = isoDateToCivil(e.target.value); if (c) setCivil(c); }}
              aria-label="Pick a date"
            />
            <button type="button" className="btn small" onClick={refetch}>↻</button>
          </div>
  );

  return (
    <>
      {!embedded && <TopBar crumbs={['My Day']} right={nav} />}

      <div className={embedded ? '' : 'app-content'} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="flex-between" style={{ alignItems: 'center' }}>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {!embedded && user?.name ? `${user.name} · ` : ''}{formatDate(civil)}
          </div>
          {embedded && nav}
        </div>

        {/* ── stat row (the consultant dashboard shows its own) ── */}
        {!embedded && (
          <div className="grid-4">
            <Stat label="Tasks due" value={counts.dueToday} />
            <Stat label="Overdue" value={counts.overdue} tone="var(--danger)" />
            <Stat label="Appointments" value={counts.appointments} />
            <Stat label="No next step" value={counts.leadsWithoutNextEvent} tone="var(--warning)" />
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 14, alignItems: 'start' }}>
          {/* ── left: the day ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <TaskGroup
              title="⚠ Overdue"
              tone="var(--danger)"
              tasks={asList(plan.overdue)}
              names={names}
              onComplete={setCompleting}
              onSnooze={snooze}
            />
            <TaskGroup title="Morning" tasks={asList(plan.morning)} names={names} onComplete={setCompleting} onSnooze={snooze} />
            <TaskGroup title="Afternoon" tasks={asList(plan.afternoon)} names={names} onComplete={setCompleting} onSnooze={snooze} />
            <TaskGroup title="Later / undated" tasks={asList(plan.later)} names={names} onComplete={setCompleting} onSnooze={snooze} />

            {!loading
              && !asList(plan.overdue).length
              && !asList(plan.morning).length
              && !asList(plan.afternoon).length
              && !asList(plan.later).length && (
              <div className="card" style={{ padding: 24 }}>
                <div className="empty-state">
                  <div className="icon">✅</div>
                  <div className="title">Nothing due</div>
                  <div className="desc">
                    No tasks for {today ? 'today' : formatDate(civil)}. Check the leads with no next
                    step on the right.
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── right: context ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="card" style={{ padding: 12 }}>
              <div className="card-label" style={{ marginBottom: 8 }}>APPOINTMENTS</div>
              {asList(plan.appointments).length === 0 ? (
                <div className="mono text-xs text-muted">None booked.</div>
              ) : (
                asList(plan.appointments).map((a, i, arr) => {
                  const meta = typeMeta(a.type);
                  return (
                    <div
                      key={a.id}
                      style={{
                        padding: '10px 0',
                        borderBottom: i === arr.length - 1 ? 'none' : '1px solid var(--border-subtle)',
                      }}
                    >
                      <div className="flex-between">
                        <span className="mono text-xs" style={{ fontWeight: 600 }}>{formatTime(a.startsAt)}</span>
                        <span className="pill" style={{ borderColor: meta.color, color: meta.color }}>
                          {meta.icon} {meta.short}
                        </span>
                      </div>
                      <div style={{ marginTop: 4, fontSize: 13 }}>
                        {names.customer(a.customerId, a.customerName, { dash: null })
                          || names.lead(a.leadId, a.leadName, { dash: null })
                          || '—'}
                      </div>
                      {a.vehicleLabel && (
                        <div className="mono text-xs text-muted">{a.vehicleLabel}</div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            <div className="card" style={{ padding: 12 }}>
              <div className="flex-between" style={{ marginBottom: 8 }}>
                <span className="card-label" style={{ color: 'var(--warning)' }}>NO NEXT STEP</span>
                <span className="mono text-xs text-muted">{asList(plan.leadsWithoutNextEvent).length}</span>
              </div>
              {asList(plan.leadsWithoutNextEvent).length === 0 ? (
                <div className="mono text-xs text-muted">Every lead has a next step. Nice.</div>
              ) : (
                asList(plan.leadsWithoutNextEvent).map((l, i, arr) => (
                  <div
                    key={l.id}
                    className="flex-between"
                    style={{
                      padding: '10px 0',
                      borderBottom: i === arr.length - 1 ? 'none' : '1px solid var(--border-subtle)',
                      gap: 8,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <Link to={`/leads/${l.id}`} style={{ fontSize: 13, fontWeight: 600 }}>
                        {l.customerName || names.lead(l.id, l.name, { dash: null }) || 'Lead'}
                      </Link>
                      <div className="mono text-xs text-muted" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {[l.vehicle, l.stage].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <button type="button" className="btn small" onClick={() => setSettingFor(l)}>+ Set</button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <CompleteTaskModal
        open={!!completing}
        task={completing}
        lead={completing?.lead || null}
        convertsToAppointment={!!completing?.convertsToAppointment}
        onClose={() => setCompleting(null)}
        onCompleted={() => { setCompleting(null); refetch(); }}
      />

      <NextEventModal
        open={!!settingFor}
        leadId={settingFor?.id || null}
        defaultAssigneeId={consultantId}
        onClose={() => setSettingFor(null)}
        onSaved={() => { setSettingFor(null); refetch(); }}
      />
    </>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="card" style={{ padding: 12 }}>
      <div className="card-label">{label}</div>
      <div className="metric-value" style={tone && value > 0 ? { color: tone } : undefined}>
        {value ?? 0}
      </div>
    </div>
  );
}

function TaskGroup({ title, tone, tasks, names, onComplete, onSnooze }) {
  if (!tasks?.length) return null;
  return (
    <div className="card" style={{ padding: 12 }}>
      <div className="flex-between" style={{ marginBottom: 6 }}>
        <span className="card-label" style={tone ? { color: tone } : undefined}>{title}</span>
        <span className="mono text-xs text-muted">{tasks.length}</span>
      </div>
      {tasks.map((t, i) => (
        <TaskRow
          key={t.id}
          task={t}
          names={names}
          last={i === tasks.length - 1}
          onComplete={onComplete}
          onSnooze={onSnooze}
        />
      ))}
    </div>
  );
}

function TaskRow({ task, names, last, onComplete, onSnooze }) {
  const rel = relativeDue(task.dueAt);
  const who = task.leadId
    ? names.lead(task.leadId, task.leadName, { dash: null })
    : names.customer(task.customerId, task.customerName, { dash: null });
  const href = task.leadId ? `/leads/${task.leadId}`
    : (task.customerId ? `/customers/${task.customerId}` : null);

  return (
    <div
      className="flex-between"
      style={{
        padding: '10px 0',
        gap: 12,
        borderBottom: last ? 'none' : '1px solid var(--border-subtle)',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div className="flex-row" style={{ alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {task.dueAt && (
            <span className="mono text-xs" style={{ fontWeight: 600 }}>{formatTime(task.dueAt)}</span>
          )}
          <span style={{ fontSize: 13, fontWeight: 600 }}>{task.title}</span>
          {task.reasonLabel && <span className="pill">{task.reasonLabel}</span>}
        </div>
        <div className="mono text-xs text-muted" style={{ marginTop: 3 }}>
          {href ? <Link to={href}>{who || 'open record'}</Link> : (who || '—')}
          {task.vehicleLabel ? ` · ${task.vehicleLabel}` : ''}
          {' · '}
          <span style={{ color: rel.overdue ? 'var(--danger)' : 'var(--text-muted)', fontWeight: rel.overdue ? 600 : 400 }}>
            {rel.label}
          </span>
        </div>
      </div>

      <div className="flex-row" style={{ gap: 6, flexShrink: 0 }}>
        <button type="button" className="btn small primary" onClick={() => onComplete(task)}>✓ Complete</button>
        <button type="button" className="btn small" onClick={() => onSnooze(task)}>Snooze</button>
      </div>
    </div>
  );
}
